import { useMemo } from "react"

import { useMatch, useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { OpenInNewRoundedIcon } from "@l2r-front/l2r-icons"
import { RoadDetailInfo } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { OBJECT_TYPE_FILTER } from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { APP_DOCUMENT, MODULE_DOCUMENTS, ModulesInfo, NETWORK_ATTACHMENT, ROAD_ATTACHMENT, SECTION_ATTACHMENT } from "../../constants/modules"
import { LINEAR_LOCATION_END, LINEAR_LOCATION_START } from "../../constants/urlParams"
import { useDocumentStateContext } from "../../contexts/DocumentContext"
import { useDocuments } from "../../hooks/queries/useDocument"
import { useElementQuery } from "../../hooks/queries/useElementQuery"
import { useElementTypeQuery } from "../../hooks/queries/useElementTypeQuery"
import { useVerticalSignTypeQueries } from "../../hooks/queries/useVerticalSignTypeQuery"

import { SectionLength } from "../SectionLength"

import * as Styled from "./DocumentAttachedObjectSidebarContent.styled"

export function DocumentAttachedObjectSidebarContent(props) {

    const {
        className,
    } = props

    const { road } = useParams()
    const matchSection = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/${SECTION_ATTACHMENT}`)
    const match = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/:attachmentType/*`)
    const matchNetwork = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/attached/${NETWORK_ATTACHMENT}`)
    const { apiFilters } = useDocumentStateContext()
    const attachmentType = matchNetwork ? NETWORK_ATTACHMENT : matchSection ? SECTION_ATTACHMENT : match ? match.params.attachmentType : null
    const { t } = useTranslation([I18N_NAMESPACE])
    const { attachmentId } = useParams()
    const isMobile = useIsMobileDevice()

    const { data: attachedObject } = useElementQuery(attachmentType, attachmentId, { enabled: !!attachmentType && !!attachmentId })
    const verticalSignsTypes = useVerticalSignTypeQueries(attachedObject?.vertical_signs, { enabled: !!attachedObject?.vertical_signs?.length })

    const attachedObjectType = useMemo(() => {
        if (attachedObject && attachmentType) {
            return ModulesInfo?.[attachmentType]?.getElementType?.(attachedObject, verticalSignsTypes.map(q => q.data)) || null
        }
        return null
    }, [attachmentType, attachedObject, verticalSignsTypes])

    const { data: attachedObjectNetworkType } = useElementTypeQuery(attachmentType, attachedObjectType, { enabled: !!attachedObjectType && !!attachmentId })
    const { getParam } = useUrlParams()

    const linearLocationStart = getParam(LINEAR_LOCATION_START)
    const linearLocationEnd = getParam(LINEAR_LOCATION_END)

    const { data: documents, isLoading } = useDocuments(
        {
            ...(road && { linear_location_road: road }),
            ...(attachmentId && { attached_object_uuid: attachmentId }),
            ...(apiFilters && { ...apiFilters }),
            ...(attachmentType !== SECTION_ATTACHMENT && attachmentType !== ROAD_ATTACHMENT) && { [OBJECT_TYPE_FILTER]: attachmentType },
            ...(linearLocationStart && { linear_location_start: linearLocationStart }),
            ...(linearLocationEnd && { linear_location_end: linearLocationEnd }),
            ...((attachmentType === SECTION_ATTACHMENT || attachmentType === ROAD_ATTACHMENT) && { [OBJECT_TYPE_FILTER]: attachmentType.toUpperCase() }),
        })

    const subtitle = useMemo(() => {
        return ModulesInfo?.[attachmentType]?.getDetailsTitle(t, attachedObject, attachedObjectNetworkType, attachedObjectType)
    }, [attachedObjectType, attachmentType, attachedObject, t, attachedObjectNetworkType])

    const moduleUrl = useMemo(() => {
        if (attachmentType && attachmentId && road) {
            return `/${match?.params?.networkSlug}/${ModulesInfo?.[attachmentType]?.getItemModuleRoute(road, attachmentId, attachedObject)}` || null
        }
        return null
    }, [attachmentType, attachmentId, attachedObject, match, road])

    return (
        <Styled.Container className={className} id="documentAttachedObjectSidebarContent-container" isMobile={isMobile}>
            <Styled.HeaderWrapper isMobile={isMobile}>
                {moduleUrl ? <Styled.Link href={moduleUrl} underline="none">
                    <Styled.LinkText variant="H2">{subtitle}</Styled.LinkText>
                    <OpenInNewRoundedIcon />
                </Styled.Link> : <Typography variant="H2">{subtitle}</Typography>}
                {attachedObject && <Styled.RoadDetailInfo attachedObject={attachedObject} attachmentType={attachmentType} road={road} />}
                {!!matchSection && <RoadDetailInfo lengthLabel={t(I18N_NAMESPACE, "containers.documentAttachedObjectSidebar.sectionLength")} LengthComponent={<SectionLength />} road={road} />}
                {attachmentType === ROAD_ATTACHMENT && <RoadDetailInfo lengthLabel={t(I18N_NAMESPACE, "containers.documentAttachedObjectSidebar.sectionLength")} road={road} />}
            </Styled.HeaderWrapper>
            <Styled.DocumentList
                documents={documents}
                isDocumentsLoading={isLoading}
                isMobile={isMobile}
                title={t(I18N_NAMESPACE, "containers.documentAttachedObjectSidebar.subtitle")}
            />
        </Styled.Container>
    )
}

DocumentAttachedObjectSidebarContent.propTypes = {
    className: PropTypes.string,
}
